<template>
  <div>

    <el-alert
      v-if="errored"
      type="error"
      :closable="false"
      show-icon
      title="Couldn't load the fee timeline"
      description="Please try again in a moment." />

    <el-row v-else-if="isEmpty">
      <el-col :span="24" align="center">
        <i class="text-muted">{{ reason || 'No fee timeline yet for this sharer.' }}</i>
      </el-col>
    </el-row>

    <div v-else v-loading="loading">

      <!-- Totals header -->
      <el-row :gutter="16" class="timeline-totals">
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label">Billed to date</div>
          <div class="timeline-totals__value">{{ totalsDisplay.billedToDate }}</div>
        </el-col>
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label timeline-totals__label--muted">Projected remaining</div>
          <div class="timeline-totals__value timeline-totals__value--muted">{{ totalsDisplay.projectedRemaining }}</div>
        </el-col>
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label">Lifetime</div>
          <div class="timeline-totals__value timeline-totals__value--bold">{{ totalsDisplay.lifetime }}</div>
        </el-col>
      </el-row>
      <p class="timeline-caption text-muted">
        Amounts left of "Lifetime" are billed actuals; "Projected remaining" is forward-estimated and may change.
      </p>

      <!-- Open-ended note -->
      <el-alert
        v-if="openEnded"
        type="info"
        :closable="false"
        show-icon
        title=""
        description="This agreement is open-ended — the projection is capped at 240 months." />

      <el-table
        :data="displayRows"
        :max-height="520"
        style="width:100%"
        :row-class-name="rowClassName">

        <!-- Expand column -->
        <el-table-column type="expand">
          <template slot-scope="scope">
            <div class="timeline-expand">
              <p class="timeline-expand__caption text-muted">
                Determinant values are shown for explanation only and are never summed.
              </p>

              <el-row :gutter="8">
                <!-- Tiering group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Tier</span>
                  <span class="timeline-expand__val">{{ scope.row.tier_seq !== null && scope.row.tier_seq !== undefined ? scope.row.tier_seq : '—' }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">{{ positionKeyLabel(scope.row) }}</span>
                  <span class="timeline-expand__val">{{ scope.row._positionLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Billing mode</span>
                  <span class="timeline-expand__val">{{ scope.row.billing_mode !== null && scope.row.billing_mode !== undefined ? scope.row.billing_mode : '—' }}</span>
                </el-col>

                <!-- Fees group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Telco fee</span>
                  <span class="timeline-expand__val">{{ scope.row._telcoFeeLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">MW rate</span>
                  <span class="timeline-expand__val">{{ scope.row._mwRateLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">RRU rate</span>
                  <span class="timeline-expand__val">{{ scope.row._rruRateLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">RRU set</span>
                  <span class="timeline-expand__val">{{ scope.row.rru_set !== null && scope.row.rru_set !== undefined ? scope.row.rru_set : '—' }}</span>
                </el-col>

                <!-- Sharer-specific group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">CAPEX</span>
                  <span class="timeline-expand__val">{{ scope.row._capexLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">OPEX</span>
                  <span class="timeline-expand__val">{{ scope.row._opexLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Discount</span>
                  <span class="timeline-expand__val">{{ scope.row._discountLabel }}</span>
                </el-col>

                <!-- First-sharer-only -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">First-sharer-only</span>
                  <span class="timeline-expand__val">{{ scope.row._firstSharerLabel }}</span>
                </el-col>

                <!-- Invoice info (invoiced rows only) -->
                <template v-if="scope.row.status === 'invoiced'">
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Invoice</span>
                    <span class="timeline-expand__val">{{ scope.row.invoice_id !== null && scope.row.invoice_id !== undefined ? scope.row.invoice_id : '—' }}</span>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Invoiced</span>
                    <span class="timeline-expand__val">{{ scope.row.invoiced_at ? formatDate(scope.row.invoiced_at) : '—' }}</span>
                  </el-col>
                </template>

                <!-- Prorated info (prorated rows only) -->
                <template v-if="scope.row.isProrated">
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Charged</span>
                    <span class="timeline-expand__val">{{ scope.row.chargeRangeLabel }}</span>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Full month</span>
                    <span class="timeline-expand__val">{{ scope.row.fullMonthLabel }}</span>
                  </el-col>
                </template>

              </el-row>
            </div>
          </template>
        </el-table-column>

        <!-- Period column -->
        <el-table-column label="Period" min-width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.__boundary" class="timeline-boundary-marker">Projected from here →</span>
            {{ scope.row.periodLabel }}
          </template>
        </el-table-column>

        <!-- Status column -->
        <el-table-column label="Status" width="160">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.status === 'invoiced'"
              type="success"
              size="small"
              effect="light">
              <i class="fa fa-check-circle"></i> Invoiced
            </el-tag>
            <el-tag
              v-else-if="scope.row.status === 'billed-uninvoiced'"
              type="warning"
              size="small"
              effect="light">
              <i class="fa fa-clock"></i> Billed
            </el-tag>
            <el-tag
              v-else
              type="info"
              size="small"
              effect="light">
              <i class="fa fa-hourglass-half"></i> Projected
            </el-tag>
          </template>
        </el-table-column>

        <!-- Amount column -->
        <el-table-column label="Amount" align="right" class-name="fee-cell" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.amountLabel }}
            <el-tooltip
              v-if="scope.row.isProrated"
              content="Prorated — partial month; see details for the charged range."
              placement="top">
              <el-tag
                type="info"
                size="mini"
                effect="light"
                class="timeline-flag"
                aria-label="Prorated — partial month; see details for the charged range.">
                <i class="fa fa-adjust"></i>
              </el-tag>
            </el-tooltip>
            <el-tooltip
              v-if="scope.row.isLocked"
              content="Rate locked — this month uses a manual override, not the calculated rate."
              placement="top">
              <el-tag
                type="warning"
                size="mini"
                effect="light"
                class="timeline-flag"
                aria-label="Rate locked — this month uses a manual override, not the calculated rate.">
                <i class="fa fa-lock"></i>
              </el-tag>
            </el-tooltip>
          </template>
        </el-table-column>

      </el-table>
    </div>
  </div>
</template>

<script>
import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'

import moment from 'moment'

export default {
  name: 'sharer-fee-timeline',

  mixins: [NumberMixins, PostMixins, DateMixins],

  props: {
    id: {
      type: [Number, String],
      required: true
    },
    started: {
      type: String,
      default: null
    }
  },

  data: function () {
    return {
      rows: [],
      totals: null,
      window: null,
      licensee: null,
      reason: null,
      loading: false,
      loaded: false,
      errored: false
    }
  },

  computed: {
    isEmpty: function () {
      return this.loaded && !this.errored && this.rows.length === 0
    },

    openEnded: function () {
      return !!(this.window && this.window.openEnded)
    },

    boundaryIndex: function () {
      for (var i = 0; i < this.rows.length; i++) {
        if (this.rows[i].source === 'projection') {
          return i
        }
      }
      return -1
    },

    hasProjection: function () {
      return this.boundaryIndex >= 0
    },

    displayRows: function () {
      var self = this
      var boundary = self.boundaryIndex
      return self.rows.map(function (row, idx) {
        var periodLabel = ''
        if (row.period) {
          periodLabel = moment(row.period + '-01').format('MMM YYYY')
        }

        var amountLabel
        if (row.billed_amount === null || row.billed_amount === undefined) {
          amountLabel = '—'
        } else {
          amountLabel = self.formatMoney(row.billed_amount, 2)
        }

        var fullMonthLabel
        if (row.full_month_amount === null || row.full_month_amount === undefined) {
          fullMonthLabel = '—'
        } else {
          fullMonthLabel = self.formatMoney(row.full_month_amount, 2)
        }

        var chargeRangeLabel = ''
        if (row.prorated && row.charge_from && row.charge_to) {
          chargeRangeLabel = self.formatDate(row.charge_from) + ' – ' + self.formatDate(row.charge_to)
        }

        // Money determinants
        var _telcoFeeLabel = row.telco_fee !== null && row.telco_fee !== undefined
          ? self.formatMoney(row.telco_fee, 2) : '—'
        var _mwRateLabel = row.mw_rate !== null && row.mw_rate !== undefined
          ? self.formatMoney(row.mw_rate, 2) : '—'
        var _rruRateLabel = row.rru_rate !== null && row.rru_rate !== undefined
          ? self.formatMoney(row.rru_rate, 2) : '—'
        var _capexLabel = row.capex !== null && row.capex !== undefined
          ? self.formatMoney(row.capex, 2) : '—'
        var _opexLabel = row.opex !== null && row.opex !== undefined
          ? self.formatMoney(row.opex, 2) : '—'

        // Discount: % or money
        var _discountLabel
        if (row.discount === null || row.discount === undefined) {
          _discountLabel = '—'
        } else if (row.discount_percentage) {
          _discountLabel = row.discount + '%'
        } else {
          _discountLabel = self.formatMoney(row.discount, 2)
        }

        // First-sharer-only boolean
        var _firstSharerLabel
        if (row.first_sharer_only === null || row.first_sharer_only === undefined) {
          _firstSharerLabel = '—'
        } else {
          _firstSharerLabel = self.formatBoolean(row.first_sharer_only) || 'No'
        }

        // XOL-376 Inc 2: post-clamp billed position, surfaced inline when it differs
        // from the raw position. null billed_position (override) or equal → raw only.
        var _positionLabel
        if (row.position === null || row.position === undefined) {
          _positionLabel = '—'
        } else if (row.billed_position !== null && row.billed_position !== undefined &&
                   row.billed_position !== row.position) {
          _positionLabel = row.position + ' · billed at #' + row.billed_position
        } else {
          _positionLabel = row.position
        }

        return Object.assign({}, row, {
          periodLabel: periodLabel,
          amountLabel: amountLabel,
          fullMonthLabel: fullMonthLabel,
          isProjection: row.source === 'projection',
          isProrated: !!row.prorated,
          isLocked: !!row.rate_locked,
          chargeRangeLabel: chargeRangeLabel,
          __boundary: boundary >= 0 && idx === boundary,
          _telcoFeeLabel: _telcoFeeLabel,
          _mwRateLabel: _mwRateLabel,
          _rruRateLabel: _rruRateLabel,
          _capexLabel: _capexLabel,
          _opexLabel: _opexLabel,
          _discountLabel: _discountLabel,
          _firstSharerLabel: _firstSharerLabel,
          _positionLabel: _positionLabel
        })
      })
    },

    totalsDisplay: function () {
      if (!this.totals) {
        return { billedToDate: '—', projectedRemaining: '—', lifetime: '—' }
      }
      return {
        billedToDate: this.formatMoney(this.totals.billedToDate, 2),
        projectedRemaining: this.formatMoney(this.totals.projectedRemaining, 2),
        lifetime: this.formatMoney(this.totals.lifetime, 2)
      }
    }
  },

  created: function () {
    this.load()
  },

  methods: {
    load: function () {
      if (!this.id) {
        this.loaded = true
        return
      }

      var self = this
      this.loading = true
      this.errored = false
      this.loaded = false

      this.fetchData(
        'licensees/' + this.id + '/fee-ledger',
        function (data) {
          var d = data && typeof data === 'object' && !Array.isArray(data) ? data : {}
          self.rows = Array.isArray(d.rows) ? d.rows : []
          self.totals = d.totals || null
          self.window = d.window || null
          self.licensee = d.licensee || null
          self.reason = d.reason || null
          self.loading = false
          self.loaded = true
        },
        'fee timeline',
        function (_err) {
          self.errored = true
          self.loading = false
          self.loaded = true
        }
      )
    },

    // XOL-319: under "Qty" billing the Position determinant is really a sharer
    // count, so label it "Count"; "Position" everywhere else. Case-insensitive
    // so 'Qty'/'qty' both flip.
    positionKeyLabel: function (row) {
      var mode = row && row.billing_mode
      return String(mode).toLowerCase() === 'qty' ? 'Count' : 'Position'
    },

    rowClassName: function (obj) {
      var row = obj.row
      var classes = []
      if (row.isProjection) {
        classes.push('timeline-row--projection')
      }
      if (row.__boundary) {
        classes.push('timeline-row--boundary')
      }
      return classes.join(' ')
    }
  }
}
</script>

<style scoped>
.timeline-totals {
  margin-bottom: 8px;
}

.timeline-totals__stat {
  padding: 8px 0;
}

.timeline-totals__label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 2px;
}

.timeline-totals__label--muted {
  color: #909399;
}

.timeline-totals__value {
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}

.timeline-totals__value--muted {
  color: #909399;
}

.timeline-totals__value--bold {
  font-weight: 600;
}

.timeline-caption {
  font-size: 12px;
  margin-bottom: 8px;
}

.timeline-boundary-marker {
  display: block;
  font-size: 11px;
  color: #909399;
  margin-bottom: 2px;
}

.timeline-flag {
  margin-left: 4px;
  cursor: default;
}

::v-deep .fee-cell {
  font-variant-numeric: tabular-nums;
}

::v-deep .timeline-row--projection td {
  background: #fafafa;
  color: #909399;
}

::v-deep .timeline-row--boundary td {
  border-top: 2px solid #dcdfe6 !important;
}

.timeline-expand {
  padding: 8px 16px;
}

.timeline-expand__caption {
  font-size: 12px;
  margin-bottom: 8px;
}

.timeline-expand__item {
  margin-bottom: 6px;
  display: flex;
  gap: 6px;
}

.timeline-expand__key {
  font-size: 12px;
  min-width: 100px;
  flex-shrink: 0;
}

.timeline-expand__val {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
</style>
